/**
 * Mixin that handles the creation and management of map markers.
 *
 * Responsibilities:
 * - Creating SVG-based custom markers
 * - Managing marker states (active, inactive)
 * - Handling marker visibility
 * - Providing MarkerClusterer compatibility methods
 *
 * The mixin expects the following to be available in the component:
 * - this.map: Google Maps instance
 * - this.options.MarkerColor: Color for the marker SVG
 * - this.markers: Array to store created markers
 */
export default {
  data: () => ({
    iconSvgPath:
      'M17 0.524292C12.6255 0.529668 8.43169 2.34222 5.33846 5.56433C2.24522 8.78645 0.505178 13.155 0.500018 17.7118C0.494778 21.4356 1.66249 25.0583 3.82402 28.0243C3.82402 28.0243 4.27402 28.6415 4.34752 28.7305L17 44.2743L29.6585 28.7227C29.7245 28.6399 30.176 28.0243 30.176 28.0243L30.1775 28.0196C32.338 25.0549 33.5051 21.4339 33.5 17.7118C33.4949 13.155 31.7548 8.78645 28.6616 5.56433C25.5683 2.34222 21.3745 0.529668 17 0.524292ZM17 23.9618C15.8133 23.9618 14.6533 23.5952 13.6666 22.9085C12.6799 22.2217 11.9109 21.2456 11.4567 20.1036C11.0026 18.9615 10.8838 17.7049 11.1153 16.4925C11.3468 15.2801 11.9183 14.1665 12.7574 13.2924C13.5965 12.4183 14.6656 11.823 15.8295 11.5819C16.9934 11.3407 18.1998 11.4645 19.2961 11.9375C20.3925 12.4106 21.3295 13.2117 21.9888 14.2395C22.6481 15.2673 23 16.4757 23 17.7118C22.998 19.3688 22.3653 20.9573 21.2405 22.1289C20.1157 23.3006 18.5907 23.9597 17 23.9618Z',
  }),

  methods: {
    createMarkerSvg(width = '34', height = '45', opacity = '1') {
      const markerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      markerSvg.setAttribute('viewBox', '0 0 34 45')
      markerSvg.setAttribute('width', width)
      markerSvg.setAttribute('height', height)

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute('d', this.iconSvgPath)
      path.setAttribute('fill', this.options.MarkerColor)
      path.setAttribute('fill-opacity', opacity)
      markerSvg.appendChild(path)

      return markerSvg
    },

    createPinElement(opacity, scale) {
      const markerSvg = this.createMarkerSvg('34', '45', opacity)
      return new google.maps.marker.PinElement({
        glyph: markerSvg,
        scale: scale * 2,
        background: 'transparent',
        borderColor: 'transparent'
      })
    },

    createMarker(location) {
      const position = new google.maps.LatLng(
        parseFloat(location.Latitude),
        parseFloat(location.Longitude)
      )

      const pinElement = this.createPinElement('1', 0.7)

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position,
        content: pinElement.element,
        title: location.Title.replace(/<{1}[^<>]{1,}>{1}/g, '')
      })

      // Store location data on marker for reference
      marker.locationData = location

      // Add compatibility methods for MarkerClusterer
      marker.getPosition = () => position
      marker.getVisible = () => !marker.hidden
      marker.setVisible = (visible) => {
        if (visible) {
          marker.map = marker.oldMap
          delete marker.oldMap
          marker.hidden = false
        } else {
          marker.oldMap = marker.map
          marker.map = null
          marker.hidden = true
        }
      }

      return marker
    },

    changeMarker(marker, action) {
      if (!this.showMarkerOverlay) {
        this.markers.forEach((m) => {
          m.content = this.createPinElement(1, 0.7).element
        })
      }

      if (action === 'active') {
        this.markers.forEach((m) => {
          m.content = this.createPinElement(0.5, 0.5).element
        })
        marker.content = this.createPinElement(1, 1).element
      }
    },

    addMarkers() {
      if (!Array.isArray(this.geoLocations)) {
        return
      }

      this.markers = this.geoLocations.map((location) => {
        const marker = this.createMarker(location)
        marker.addListener('gmp-click', () => {
          this.onSelectMarker(this.map, marker)
        })
        return marker
      })

      this.map.addListener('click', () => {
        if (this.showMarkerOverlay) {
          this.closeMapContent()
        }
      })

      this.map.addListener('drag', () => {
        if (this.showMarkerOverlay) {
          this.closeMapContent()
        }
      })

      this.initializeClusterer()
    },
  }
}
